import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { GROUP_LABELS } from "@/components/ExpenseFormDialog";

const emptyForm = { code: "", name: "", group: "operational", active: true };

export const ExpenseAccountsAdmin = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error(t("field_required")); return; }
    try {
      if (editing) await api.put(`/expense-accounts/${editing.id}`, form);
      else await api.post("/expense-accounts", form);
      toast.success(t("account_saved"));
      setOpen(false);
      await cfg.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-4" data-testid="expense-accounts-admin">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{t("accounts_desc")}</p>
        <Button data-testid="add-account-button" className="h-9 gap-2 rounded-full bg-orange-500 hover:bg-orange-600"
          onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}>
          <Plus className="h-4 w-4" /> {t("add_account")}
        </Button>
      </div>

      <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">{t("account_code")}</th>
              <th className="px-4 py-3">{t("account_name")}</th>
              <th className="px-4 py-3">{t("account_group")}</th>
              <th className="px-4 py-3">{t("status")}</th>
              <th className="px-4 py-3 text-right" />
            </tr>
          </thead>
          <tbody>
            {(cfg?.expenseAccounts || []).map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`account-row-${a.code}`}>
                <td className="px-4 py-3 mono font-semibold text-slate-800">{a.code}</td>
                <td className="px-4 py-3 text-slate-700">{a.name}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{GROUP_LABELS[a.group]}</td>
                <td className="px-4 py-3 text-xs">
                  <span className={a.active ? "font-semibold text-emerald-600" : "text-slate-400"}>
                    {t(a.active ? "active" : "inactive")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`edit-account-${a.code}`} className="p-1.5 text-slate-400 hover:text-slate-900"
                    onClick={() => { setEditing(a); setForm({ ...a }); setOpen(true); }}>
                    <Pencil className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_account")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("account_code")}</Label>
              <Input data-testid="account-code-input" className="mono" value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("account_name")}</Label>
              <Input data-testid="account-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("account_group")}</Label>
              <select data-testid="account-group-select" value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                {Object.entries(GROUP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="account-active-checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              {t("active")}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="save-account-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseAccountsAdmin;
